/**
 * SetupPage Component
 * First-run setup page for creating the initial super admin
 * Only accessible when no super_admin exists in the database
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Card } from '../../components/common/Card';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import api from '../../services/api';
import { useDomainRedirect } from '../../hooks';

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

const getPasswordStrength = (password: string): PasswordStrength => {
  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2) return { score, label: 'Weak', color: 'bg-danger-500' };
  if (score <= 4) return { score, label: 'Fair', color: 'bg-yellow-500' };
  if (score <= 5) return { score, label: 'Good', color: 'bg-primary-500' };
  return { score, label: 'Strong', color: 'bg-success-500' };
};

export const SetupPage: React.FC = () => {
  const navigate = useNavigate();

  // Redirect to OKR Hub domain if on marketing site
  useDomainRedirect();

  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [setupRequired, setSetupRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);

  const [formData, setFormData] = useState({
    setupKey: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>('');

  // Check if setup is required on mount
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        const response = await api.get('/setup/status');
        setSetupRequired(response.data.setupRequired);
        if (!response.data.setupRequired) {
          // Setup already completed, redirect to login
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
        setApiError('Failed to check setup status');
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkSetupStatus();
  }, [navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.setupKey) {
      newErrors.setupKey = 'Setup key is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else {
      if (formData.password.length < 12) {
        newErrors.password = 'Password must be at least 12 characters';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one lowercase letter';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one number';
      } else if (!/[^A-Za-z0-9]/.test(formData.password)) {
        newErrors.password = 'Password must contain at least one special character';
      }
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setApiError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setApiError('');

    try {
      await api.post('/setup/super-admin', {
        setupKey: formData.setupKey,
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });

      setSetupComplete(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      console.error('Setup error:', error);
      const err = error as { response?: { status?: number; data?: { error?: string } }; message?: string };
      if (err.response?.status === 403) {
        setApiError('Invalid setup key. Please check and try again.');
      } else if (err.response?.status === 409) {
        setApiError('Email already in use.');
      } else if (err.response?.status === 404) {
        setApiError('Setup has already been completed.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setApiError(
          err.response?.data?.error ||
          err.message ||
          'Setup failed. Please try again.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Loading state
  if (isCheckingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-stone-100">Checking setup status...</p>
        </div>
      </div>
    );
  }

  // Setup not required
  if (!setupRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800">
        <Card className="p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Setup Complete</h2>
          <p className="text-stone-600">
            Super admin has already been configured. Redirecting to login...
          </p>
        </Card>
      </div>
    );
  }

  // Setup complete success
  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800">
        <Card className="p-8 max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Setup Successful!</h2>
          <p className="text-stone-600">
            Super admin account created. Redirecting to login...
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 via-primary-600 to-stone-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary-700 rounded-xl">
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Platform Setup</h1>
          <p className="text-stone-200">Create your Super Admin account</p>
        </div>

        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {apiError && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{apiError}</span>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
              <p className="font-medium">Security Notice</p>
              <p className="mt-1 text-amber-700">
                Enter the setup key from your environment configuration to proceed.
              </p>
            </div>

            <Input
              label="Setup Key"
              type="password"
              name="setupKey"
              value={formData.setupKey}
              onChange={handleChange}
              error={errors.setupKey}
              placeholder="Enter your setup key"
              required
            />

            <div className="border-t pt-5">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Admin Account Details</h3>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <Input
                  label="First Name"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  placeholder="John"
                  required
                />
                <Input
                  label="Last Name"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  placeholder="Doe"
                  required
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="admin@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                placeholder="Create a strong password"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-stone-500 hover:text-stone-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {formData.password && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${passwordStrength.color} transition-all duration-300`}
                      style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-stone-600">
                    {passwordStrength.label}
                  </span>
                </div>
                <ul className="text-xs text-stone-500 space-y-1">
                  <li className={formData.password.length >= 12 ? 'text-success-600' : ''}>
                    {formData.password.length >= 12 ? '✓' : '○'} At least 12 characters
                  </li>
                  <li className={/[A-Z]/.test(formData.password) ? 'text-success-600' : ''}>
                    {/[A-Z]/.test(formData.password) ? '✓' : '○'} One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.password) ? 'text-success-600' : ''}>
                    {/[a-z]/.test(formData.password) ? '✓' : '○'} One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(formData.password) ? 'text-success-600' : ''}>
                    {/[0-9]/.test(formData.password) ? '✓' : '○'} One number
                  </li>
                  <li className={/[^A-Za-z0-9]/.test(formData.password) ? 'text-success-600' : ''}>
                    {/[^A-Za-z0-9]/.test(formData.password) ? '✓' : '○'} One special character
                  </li>
                </ul>
              </div>
            )}

            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              placeholder="Confirm your password"
              autoComplete="new-password"
              required
            />

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Creating Account...
                </span>
              ) : (
                'Create Super Admin Account'
              )}
            </Button>
          </form>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-stone-200">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-primary-300 hover:text-primary-200 font-medium"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
